/**
 * WorkspaceUpsellModal — Mini modal de upsell que aparece SOLO en momentos
 * de friccion alta del Plan Gratis del Workspace:
 *
 *   - trigger="quota_full"  → tras 403 free_quota_exceeded (3er trabajo).
 *   - trigger="type_locked" → tras 403 type_locked (intento de cambiar
 *     tipo a un Trabajo ya fijado) o tras agotar el cambio del mes.
 *
 * Reglas de "no molestar":
 *   - Solo se muestra UNA VEZ por sesion por tipo de trigger
 *     (sessionStorage flag). Si el usuario lo cierra, no vuelve a aparecer
 *     en esta sesion aunque vuelva a tocar el mismo 403.
 *   - El toast de error sigue apareciendo siempre (feedback inmediato).
 *   - Cierre con X, click fuera (overlay) o tecla Escape.
 *
 * Conversion path: boton "Actualizar a Premium" usa el helper compartido
 * `navigateToPremium()` (mismo que los toasts globales) -> scroll suave
 * al bloque `[data-testid="premium-plan-section"]` o navega a la pagina.
 */
import React, { useEffect } from 'react';
import { X, Crown, Check, Sparkles, Lock, AlertCircle } from 'lucide-react';
import { navigateToPremium } from '../utils/navigateToPremium';
import { useLanguage } from '../context/LanguageContext';

const SESSION_KEY_PREFIX = 'damyxia_workspace_upsell_shown__';

// API publica: marca el flag y resuelve si el modal puede mostrarse en esta
// sesion. El llamador debe abrir el modal solo si esto retorna true.
export const shouldShowWorkspaceUpsell = (trigger) => {
  if (!trigger) return false;
  try {
    const k = SESSION_KEY_PREFIX + trigger;
    if (sessionStorage.getItem(k)) return false;
    sessionStorage.setItem(k, '1');
    return true;
  } catch {
    return true;
  }
};

const COPIES = {
  es: {
    quota_full: {
      title: 'Llegaste al limite del Plan Gratis',
      icon: <AlertCircle className="w-7 h-7" />,
      lead: 'Este mes ya creaste tus 2 Trabajos. Para crear mas (sin esperar al mes que viene) actualiza a Premium.',
    },
    type_locked: {
      title: 'Este Trabajo ya fijo su tipo base',
      icon: <Lock className="w-7 h-7" />,
      lead: 'En el Plan Gratis cada Trabajo solo puede cambiar de tipo una vez. Premium te da tipos ilimitados al instante.',
    },
    benefitsTitle: 'Con Premium obtenes:',
    benefits: [
      'Trabajos ilimitados por mes',
      'Cambios de tipo ilimitados (sin lock)',
      'Vision GPT-4o + Workspace Auto',
      'Compartir Trabajos con link publico',
      'Soporte prioritario',
    ],
    cta: 'Actualizar a Premium',
    dismiss: 'Ahora no',
    notIntrusive: 'No volveremos a mostrarte esto en esta sesion.',
  },
  en: {
    quota_full: {
      title: "You've hit the Free Plan limit",
      icon: <AlertCircle className="w-7 h-7" />,
      lead: 'You already created your 2 Works this month. To create more right now (without waiting), upgrade to Premium.',
    },
    type_locked: {
      title: 'This Work has already locked its base type',
      icon: <Lock className="w-7 h-7" />,
      lead: 'On the Free Plan each Work can change its type only once. Premium unlocks unlimited type changes instantly.',
    },
    benefitsTitle: 'With Premium you get:',
    benefits: [
      'Unlimited Works per month',
      'Unlimited type changes (no lock)',
      'GPT-4o Vision + Workspace Auto',
      'Share Works with a public link',
      'Priority support',
    ],
    cta: 'Upgrade to Premium',
    dismiss: 'Not now',
    notIntrusive: "We won't show you this again in this session.",
  },
};

export const WorkspaceUpsellModal = ({ isOpen, trigger, onClose }) => {
  const { language } = useLanguage();
  const t = COPIES[language === 'es' ? 'es' : 'en'];

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !trigger) return null;
  const variant = t[trigger] || t.quota_full;

  const handleUpgrade = () => {
    onClose();
    // Pequeno delay para que se vea la animacion de cierre antes del scroll.
    setTimeout(() => navigateToPremium(), 80);
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="workspace-upsell-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-md rounded-2xl border border-emerald-500/30 bg-zinc-900 shadow-2xl shadow-emerald-500/10 overflow-hidden animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top gradient accent */}
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400" />

        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          aria-label={t.dismiss}
          data-testid="workspace-upsell-close-btn"
          className="absolute top-3 right-3 p-1.5 rounded-md text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 pt-7">
          {/* Header */}
          <div className="flex items-start gap-3 mb-4">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-300">
              {variant.icon}
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-white leading-tight">
                {variant.title}
              </h3>
              <p className="text-sm text-zinc-400 mt-1">{variant.lead}</p>
            </div>
          </div>

          {/* Benefits */}
          <div className="rounded-xl bg-gradient-to-br from-emerald-950/40 to-cyan-950/30 border border-emerald-500/20 p-4 mb-5">
            <div className="flex items-center gap-2 mb-3 text-emerald-300 text-sm font-medium">
              <Crown className="w-4 h-4" />
              <span>{t.benefitsTitle}</span>
            </div>
            <ul className="space-y-2">
              {t.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                  <Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-400" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              data-testid="workspace-upsell-dismiss-btn"
              className="flex-1 px-4 py-2.5 rounded-lg text-sm text-zinc-300 hover:text-white hover:bg-white/5 border border-zinc-700 transition-colors"
            >
              {t.dismiss}
            </button>
            <button
              type="button"
              onClick={handleUpgrade}
              data-testid="workspace-upsell-upgrade-btn"
              className="flex-[2] inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-zinc-950 bg-gradient-to-r from-emerald-400 to-cyan-400 hover:from-emerald-300 hover:to-cyan-300 shadow-lg shadow-emerald-500/20 transition-all"
            >
              <Sparkles className="w-4 h-4" />
              {t.cta}
            </button>
          </div>

          {/* Subtle footer note */}
          <p className="mt-3 text-xs text-zinc-500 text-center">
            {t.notIntrusive}
          </p>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceUpsellModal;
